/**
 * Creative Analyzer — Cipher PLCIE Phase 2 UI shell.
 *
 * PLCIE-SYNC-MARKER v1 — file re-touched 2026-05-07 to recover from a
 * partial Save-to-GitHub push. Safe to remove once GitHub main reflects
 * the full Phase 2 component contents.
 *
 * AFFILIATE-only. Renders:
 *   (1) 5-field intake form with a real-time per-segment naming-convention
 *       validator. Button stays disabled until every segment is valid AND
 *       every field is populated.
 *   (2) Empty results pane — four sections laid out per Section 6 of the
 *       build spec. Phase 3 wires the analysis agent; Phase 4 wires the
 *       decision gate + calibration loop.
 *
 * Naming convention (spec §4.2):
 *     [OFFER-ID]-[CHANNEL]-[CREATIVE-TYPE]-[VISUAL ASSET]-[WRITTEN COPY]
 * Example: DEBT004-META-UGC-V1H2C1-H3B1C2
 *
 * The validator is STRICT (Governing Rule §13). There is no "lighter" mode,
 * no bypass, no override. If a segment fails, the corresponding field glows
 * red with a concrete error message; if it passes, the field flips green and
 * auto-populates the adjacent dropdown where applicable.
 *
 * In Phase 2 the Run Analysis button does NOT yet call `/api/analyzer/run`
 * (that endpoint is registered in Phase 3). Clicking it transitions the
 * results pane into a "queued" skeleton state. No mocked data is displayed.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ----------------------------------------------------------------------------
// Naming convention — source of truth (spec §4.2)
// ----------------------------------------------------------------------------
const CHANNELS = ["META", "TIKTOK", "NATIVE", "GOOGLE", "YOUTUBE"];
const CREATIVE_TYPES = ["AI", "VE", "UGC"];
const OBJECTIVES = ["AWARENESS", "CLICK", "CONVERSION", "RETARGETING"];

// Visual asset block: V{n}H{n}C{n}  →  Variant / Hook / Concept
const VISUAL_RE = /^V(\d+)H(\d+)C(\d+)$/;
// Written copy block: H{n}B{n}C{n}  →  Headline / Body / CTA
const WRITTEN_RE = /^H(\d+)B(\d+)C(\d+)$/;

const MAX_VIDEO_MB = 500;
const MAX_IMAGE_MB = 20;
const ACCEPTED_VIDEO = ["video/mp4", "video/quicktime"];
const ACCEPTED_IMAGE = ["image/jpeg", "image/png"];

/** Classify a file as video or image for dissection pipeline routing. */
const classifyFile = (file) => {
  if (!file) return null;
  if (ACCEPTED_VIDEO.includes(file.type)) return "video";
  if (ACCEPTED_IMAGE.includes(file.type)) return "image";
  // Extension fallback (some systems report octet-stream)
  const n = (file.name || "").toLowerCase();
  if (n.endsWith(".mp4") || n.endsWith(".mov")) return "video";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg") || n.endsWith(".png")) return "image";
  return null;
};

const prettyMB = (bytes) => (bytes / 1024 / 1024).toFixed(1);

// ----------------------------------------------------------------------------
// Core per-segment validator
// Returns {overall: bool, segments: [{label, raw, ok, error, parsed}...]}
// ----------------------------------------------------------------------------
function validateAssetName(raw, offerIndex) {
  const labels = ["Offer ID", "Channel", "Creative Type", "Visual Asset", "Written Copy"];
  const trimmed = (raw || "").trim();
  const parts = trimmed ? trimmed.split("-") : [];

  const segs = labels.map((label, i) => ({
    label,
    raw: parts[i] || "",
    ok: false,
    error: !trimmed ? "required" : `missing ${label.toLowerCase()}`,
    parsed: null,
  }));

  // Only return upgraded results for segments that are actually present.
  for (let i = 0; i < labels.length; i++) {
    const val = parts[i];
    if (val === undefined) {
      continue;
    }
    const seg = segs[i];
    seg.raw = val;

    if (val === "") {
      seg.error = `${labels[i]} is empty`;
      continue;
    }

    if (i === 0) {
      // Offer ID must match a known active AFFILIATE offer code (case-sensitive)
      const match = offerIndex ? offerIndex[val.toUpperCase()] : null;
      if (!match) {
        seg.error = `unknown offer code "${val}" — must match an active AFFILIATE offer`;
      } else {
        seg.ok = true;
        seg.error = "";
        seg.parsed = match;
      }
    } else if (i === 1) {
      if (!CHANNELS.includes(val.toUpperCase())) {
        seg.error = `channel must be one of: ${CHANNELS.join(", ")}`;
      } else {
        seg.ok = true;
        seg.error = "";
        seg.parsed = val.toUpperCase();
      }
    } else if (i === 2) {
      if (!CREATIVE_TYPES.includes(val.toUpperCase())) {
        seg.error = `creative type must be one of: ${CREATIVE_TYPES.join(", ")}`;
      } else {
        seg.ok = true;
        seg.error = "";
        seg.parsed = val.toUpperCase();
      }
    } else if (i === 3) {
      const m = val.match(VISUAL_RE);
      if (!m) {
        seg.error = `visual asset must match pattern V{n}H{n}C{n} (e.g. V1H2C1)`;
      } else {
        seg.ok = true;
        seg.error = "";
        seg.parsed = { variant: +m[1], hook: +m[2], concept: +m[3] };
      }
    } else if (i === 4) {
      const m = val.match(WRITTEN_RE);
      if (!m) {
        seg.error = `written copy must match pattern H{n}B{n}C{n} (e.g. H3B1C2)`;
      } else {
        seg.ok = true;
        seg.error = "";
        seg.parsed = { headline: +m[1], body: +m[2], cta: +m[3] };
      }
    }
  }

  // Extra-segment guard
  if (parts.length > 5) {
    segs[4].ok = false;
    segs[4].error = `too many segments (${parts.length}) — expected exactly 5 dash-separated parts`;
  }

  const overall = segs.every((s) => s.ok);
  return { overall, segments: segs };
}

// ----------------------------------------------------------------------------
// Tiny presentational atoms (matching the existing Cipher dark-slate theme)
// ----------------------------------------------------------------------------
const Pill = ({ tone = "slate", children }) => {
  const map = {
    slate: "bg-slate-800/60 text-slate-300 border-slate-700",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    red: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${map[tone]}`}>
      {children}
    </span>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-sm font-semibold text-white tracking-wide uppercase">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------
export default function CreativeAnalyzer() {
  const { user } = useAuth();
  const isFounder = user?.role === "FOUNDER";

  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersError, setOffersError] = useState(null);

  // Founder-only seed state
  const [seedRunning, setSeedRunning] = useState(false);
  const [seedReport, setSeedReport] = useState(null);
  const [seedError, setSeedError] = useState(null);

  const [file, setFile] = useState(null);
  const [fileKind, setFileKind] = useState(null); // 'video' | 'image' | null
  const [fileError, setFileError] = useState("");

  const [assetName, setAssetName] = useState("");
  const [offerId, setOfferId] = useState("");       // offer_id (backend)
  const [channel, setChannel] = useState("");       // META | TIKTOK | ...
  const [objective, setObjective] = useState("");   // AWARENESS | ... (manual)

  // Cipher Video (typed, validated name) vs Non-Cipher Video (external ad —
  // Cipher auto-generates the name; operator picks offer + channel only).
  const [mode, setMode] = useState("CIPHER");        // "CIPHER" | "NON_CIPHER"
  const isNonCipher = mode === "NON_CIPHER";

  // "idle" | "queued" — Phase 3 adds "running" | "complete"
  const [runState, setRunState] = useState("idle");

  // ---------------- Load AFFILIATE offers once --------------------------------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setOffersLoading(true);
        setOffersError(null);
        const res = await axios.get(`${API}/analyzer/offers`);
        if (cancelled) return;
        setOffers(res.data?.offers || []);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load analyzer offers:", err);
        setOffersError(
          err?.response?.data?.detail ||
            "Failed to load offers. Your AFFILIATE access may not be provisioned."
        );
      } finally {
        if (!cancelled) setOffersLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Lookup by uppercased offer_code for the validator
  const offerIndex = useMemo(() => {
    const idx = {};
    for (const o of offers) {
      if (o.offer_code) idx[o.offer_code.toUpperCase()] = o;
    }
    return idx;
  }, [offers]);

  // ---------------- Run the validator on every keystroke ----------------------
  const validation = useMemo(
    () => validateAssetName(assetName, offerIndex),
    [assetName, offerIndex]
  );

  // ---------------- Auto-populate offer + channel from validated segments -----
  useEffect(() => {
    const s0 = validation.segments[0];
    if (s0?.ok && s0.parsed?.offer_id && s0.parsed.offer_id !== offerId) {
      setOfferId(s0.parsed.offer_id);
    }
    const s1 = validation.segments[1];
    if (s1?.ok && s1.parsed && s1.parsed !== channel) {
      setChannel(s1.parsed);
    }
    // Intentionally do NOT clear fields on validation failure — operator may
    // be mid-typing. Fields only change when a segment newly succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation]);

  // ---------------- File upload handling --------------------------------------
  const handleFile = useCallback((f) => {
    setFileError("");
    setFile(null);
    setFileKind(null);
    if (!f) return;

    const kind = classifyFile(f);
    if (!kind) {
      setFileError("Unsupported file type. Use MP4 / MOV for video or JPG / PNG for image.");
      return;
    }
    const limit = kind === "video" ? MAX_VIDEO_MB : MAX_IMAGE_MB;
    if (f.size > limit * 1024 * 1024) {
      setFileError(`File exceeds the ${limit} MB ${kind} limit (uploaded ${prettyMB(f.size)} MB).`);
      return;
    }
    setFile(f);
    setFileKind(kind);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  // ---------------- Phase 3: prediction state ---------------------------------
  // runState: "idle" | "queued" | "running" | "complete" | "error"
  const [predictionId, setPredictionId] = useState(null);
  const [prediction, setPrediction] = useState(null);   // full record from /api/analyzer/results/:id
  const [runError, setRunError] = useState(null);

  // ---------------- Can we run? -----------------------------------------------
  // Non-Cipher bypasses the typed-name validator + objective (name auto-generated,
  // objective defaulted server-side); gate on file + offer + channel only.
  const canRun =
    !!file &&
    !fileError &&
    (isNonCipher || validation.overall) &&
    !!offerId &&
    !!channel &&
    (isNonCipher || !!objective) &&
    runState === "idle";

  const onRunAnalysis = async () => {
    if (!canRun) return;
    setRunError(null);
    setPrediction(null);
    setPredictionId(null);
    setRunState("queued");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", mode);
      // Non-Cipher: no typed name (server generates it); objective defaulted.
      if (!isNonCipher) fd.append("asset_name", assetName.trim());
      fd.append("offer_id", offerId);
      fd.append("channel", channel);
      fd.append("objective", isNonCipher ? "CONVERSION" : objective);
      const res = await axios.post(`${API}/analyzer/run`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const pid = res.data?.prediction_id;
      if (!pid) throw new Error("Backend returned no prediction_id");
      setPredictionId(pid);
      setRunState("running");
    } catch (err) {
      console.error("analyzer.run failed:", err);
      setRunError(
        err?.response?.data?.detail || err?.message || "Run failed. Check backend logs."
      );
      setRunState("error");
    }
  };

  // Poll the results endpoint while the run is in flight.
  useEffect(() => {
    if (!predictionId || (runState !== "running")) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/analyzer/results/${predictionId}`);
        if (cancelled) return;
        const data = res.data || {};
        setPrediction(data);
        if (data.status === "COMPLETE") {
          setRunState("complete");
        } else if (data.status === "ERROR") {
          setRunError(data.error_detail || "Analysis errored — see backend logs.");
          setRunState("error");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("analyzer.results poll failed:", err);
      }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [predictionId, runState]);

  const onReset = () => {
    setRunState("idle");
    setFile(null);
    setFileKind(null);
    setFileError("");
    setAssetName("");
    setOfferId("");
    setChannel("");
    setObjective("");
    setPredictionId(null);
    setPrediction(null);
    setRunError(null);
  };

  // ---------------- Founder-only seed handler ---------------------------------
  const onSeed = async () => {
    if (!isFounder || seedRunning) return;
    const ok = window.confirm(
      "Seed PLCIE Phase 1 data?\n\nThis upserts 8 synthetic personas, bootstraps " +
      "creative_predictions indexes, and adds pre_launch_score / analyzer_run_date " +
      "fields to existing creatives.\n\nIdempotent — calibration_history is preserved."
    );
    if (!ok) return;
    setSeedRunning(true);
    setSeedError(null);
    setSeedReport(null);
    try {
      const res = await axios.post(`${API}/analyzer/seed`);
      setSeedReport(res.data || {});
    } catch (err) {
      console.error("analyzer.seed failed:", err);
      setSeedError(
        err?.response?.data?.detail || err?.message || "Seed failed. Check backend logs."
      );
    } finally {
      setSeedRunning(false);
    }
  };

  // Resolve the offer object in scope of the form fields (may differ from the
  // validator's parsed value if the user typed an offer ID mid-edit).
  const selectedOffer = useMemo(
    () => offers.find((o) => o.offer_id === offerId) || null,
    [offers, offerId]
  );

  return (
    <div className="space-y-6" data-testid="creative-analyzer-page">
      {/* -------------------- Page header -------------------- */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white tracking-tight">Creative Analyzer</h1>
            <Pill tone="indigo">Pre-Launch</Pill>
            <Pill tone="slate">AFFILIATE</Pill>
          </div>
          <p className="text-sm text-slate-400 max-w-2xl">
            Operator-triggered analysis. Upload a creative, confirm the intake details, and
            get a 0-100 score plus synthetic persona behavioral traces before the asset is
            pushed into any pipeline. No data flows into predictions until you explicitly
            associate.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {offersLoading ? (
            <span>Loading offers…</span>
          ) : offersError ? (
            <Pill tone="red">offers unavailable</Pill>
          ) : (
            <Pill tone="slate">{offers.length} active AFFILIATE offers</Pill>
          )}
          {isFounder && (
            <button
              type="button"
              onClick={onSeed}
              disabled={seedRunning}
              data-testid="analyzer-seed-btn"
              className="ml-2 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-500/40 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20 hover:border-indigo-400/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Seed PLCIE Phase 1 data foundation (founder only, idempotent)"
            >
              {seedRunning ? "Seeding…" : "Seed Phase 1 Data"}
            </button>
          )}
        </div>
      </header>

      {isFounder && (seedReport || seedError) && (
        <div
          data-testid="analyzer-seed-result"
          className={`text-xs rounded-lg p-3 border ${
            seedError
              ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
              : seedReport?.clean
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : "border-amber-500/30 bg-amber-500/10 text-amber-200"
          }`}
        >
          {seedError ? (
            <span>Seed failed: {seedError}</span>
          ) : (
            <>
              <span className="font-semibold">
                Phase 1 seed {seedReport?.clean ? "clean" : "completed with errors"}
              </span>
              {seedReport?.verification && (
                <span className="ml-2 text-slate-300">
                  · personas:{" "}
                  {seedReport.verification.personas?.actual_count ?? "?"}{" "}
                  · LEADGEN:{" "}
                  {seedReport.verification.personas?.by_vertical?.LEADGEN ?? "?"}
                  {" "}· ECOM:{" "}
                  {seedReport.verification.personas?.by_vertical?.ECOM ?? "?"}
                  {Array.isArray(seedReport.verification.errors) &&
                    seedReport.verification.errors.length > 0 && (
                      <> · errors: {seedReport.verification.errors.length}</>
                    )}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {offersError && (
        <div className="border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm rounded-lg p-3">
          {offersError}
        </div>
      )}

      {/* -------------------- Two-column layout -------------------- */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* --------- LEFT: intake form (2 of 5 columns on xl) --------- */}
        <div className="xl:col-span-2 space-y-6">
          {/* Mode toggle: Cipher vs Non-Cipher video */}
          <div className="flex gap-2" data-testid="analyzer-mode-toggle">
            {[
              { key: "CIPHER", label: "Cipher Video" },
              { key: "NON_CIPHER", label: "Non-Cipher Video" },
            ].map((m) => (
              <button
                type="button"
                key={m.key}
                onClick={() => setMode(m.key)}
                disabled={runState !== "idle"}
                data-testid={`analyzer-mode-${m.key}`}
                className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                  mode === m.key
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                } disabled:opacity-40`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {isNonCipher && (
            <p className="text-xs text-slate-400" data-testid="analyzer-noncipher-note">
              External ad — Cipher auto-generates the structured name after analysis and files it under the offer's{" "}
              <span className="text-emerald-300">Non-Cipher Ads</span> Drive folder.
            </p>
          )}

          <SectionCard
            title="1. Creative Asset"
            subtitle="MP4 / MOV up to 500 MB · JPG / PNG up to 20 MB"
          >
            <FileDropzone
              file={file}
              fileKind={fileKind}
              fileError={fileError}
              onSelectFile={handleFile}
              onDrop={onDrop}
              disabled={runState !== "idle"}
            />
          </SectionCard>

          {/* ── ORDER SWAPPED, 2026-09-08 (NAMING-SHARED-POOL) ────────────────
              The offer used to be "auto-populated from the asset name" —
              because the operator TYPED the name and the offer was inferred
              from it. The Founder's ruling ends the typing: "this is a
              system-assigned value specifically to remove human error; nobody
              should be able to override it by hand."

              A picker has to know which offer's names to offer, so the offer is
              chosen first and the name is chosen from what Cipher actually
              minted for it. */}
          <SectionCard
            title={isNonCipher ? "2. Offer" : "2. Offer"}
            subtitle={isNonCipher ? "Which offer does this external ad promote?" : "Choose the offer, then pick the creative Cipher named for it."}
            action={
              selectedOffer && (
                <Pill tone="green">
                  {selectedOffer.offer_code}
                </Pill>
              )
            }
          >
            <select
              data-testid="analyzer-offer-select"
              value={offerId}
              onChange={(e) => setOfferId(e.target.value)}
              disabled={runState !== "idle" || offersLoading}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            >
              <option value="">
                {offersLoading ? "Loading offers…" : "Select an AFFILIATE offer…"}
              </option>
              {offers.map((o) => (
                <option key={o.offer_id} value={o.offer_id}>
                  {o.offer_code} — {o.name} {o.vertical ? `(${o.vertical})` : ""}
                </option>
              ))}
            </select>
            {selectedOffer?.sub_category && (
              <p className="text-xs text-slate-500 mt-2">
                Niche: <span className="text-slate-300">{selectedOffer.sub_category}</span>
                {selectedOffer.status && (
                  <>
                    {" · "}
                    Status: <span className="text-slate-300">{selectedOffer.status}</span>
                  </>
                )}
              </p>
            )}
          </SectionCard>

          {!isNonCipher && (
            <SectionCard
              title="3. Asset Name"
              subtitle="Assigned by Cipher. Pick the creative you are analysing — this cannot be typed."
            >
              <AssetNameField
                value={assetName}
                onChange={setAssetName}
                validation={validation}
                disabled={runState !== "idle"}
                offerId={offerId}
              />
            </SectionCard>
          )}

          <SectionCard
            title={isNonCipher ? "3. Traffic Channel" : "4. Traffic Channel"}
            subtitle={isNonCipher ? "Where this ad runs" : "Auto-populated from the asset name — edit to override"}
          >
            <div className="grid grid-cols-5 gap-2">
              {CHANNELS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setChannel(c)}
                  disabled={runState !== "idle"}
                  data-testid={`analyzer-channel-${c}`}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    channel === c
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                      : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                  } disabled:opacity-40`}
                >
                  {c}
                </button>
              ))}
            </div>
          </SectionCard>

          {!isNonCipher && (
          <SectionCard
            title="5. Campaign Objective"
            subtitle="Drives the weight multipliers applied to the persona scoring matrix"
          >
            <div className="grid grid-cols-2 gap-2">
              {OBJECTIVES.map((o) => (
                <button
                  type="button"
                  key={o}
                  onClick={() => setObjective(o)}
                  disabled={runState !== "idle"}
                  data-testid={`analyzer-objective-${o}`}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    objective === o
                      ? "bg-indigo-500/15 border-indigo-500/40 text-indigo-200"
                      : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                  } disabled:opacity-40`}
                >
                  {o}
                </button>
              ))}
            </div>
          </SectionCard>
          )}

          <div className="sticky bottom-0 bg-slate-950/80 backdrop-blur border-t border-slate-800 -mx-5 px-5 py-4 flex items-center gap-3">
            <button
              type="button"
              onClick={onRunAnalysis}
              disabled={!canRun}
              data-testid="analyzer-run-btn"
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                canRun
                  ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                  : "bg-slate-800 text-slate-500 cursor-not-allowed"
              }`}
            >
              {runState === "idle"
                ? "Run Analysis"
                : runState === "queued"
                ? "Analysis Queued"
                : runState === "running"
                ? `Analyzing — ${prediction?.status || "running"}…`
                : runState === "complete"
                ? "Analysis Complete"
                : "Run Analysis"}
            </button>
            {runState !== "idle" && (
              <button
                type="button"
                onClick={onReset}
                data-testid="analyzer-reset-btn"
                className="px-4 py-3 rounded-lg text-sm font-medium bg-slate-900 border border-slate-800 text-slate-300 hover:text-white"
              >
                Reset
              </button>
            )}
          </div>

          {!canRun && runState === "idle" && (
            <ReadinessChecklist
              file={file}
              fileError={fileError}
              validation={validation}
              offerId={offerId}
              channel={channel}
              objective={objective}
            />
          )}
        </div>

        {/* --------- RIGHT: results shell (3 of 5 columns on xl) --------- */}
        <div className="xl:col-span-3 space-y-6">
          <ResultsShell
            runState={runState}
            fileKind={fileKind}
            selectedOffer={selectedOffer}
            channel={channel}
            objective={objective}
            assetName={assetName}
            prediction={prediction}
            runError={runError}
          />
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// File dropzone (inline; no new deps)
// ----------------------------------------------------------------------------
const FileDropzone = ({ file, fileKind, fileError, onSelectFile, onDrop, disabled }) => {
  const [hover, setHover] = useState(false);
  return (
    <div>
      <label
        data-testid="analyzer-file-dropzone"
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => { setHover(false); onDrop(e); }}
        className={`flex flex-col items-center justify-center gap-2 py-8 px-4 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
          hover
            ? "border-emerald-500/60 bg-emerald-500/5"
            : file
              ? "border-emerald-500/40 bg-emerald-500/5"
              : "border-slate-700 bg-slate-950 hover:border-slate-600"
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      >
        <input
          type="file"
          accept=".mp4,.mov,.jpg,.jpeg,.png,video/mp4,video/quicktime,image/jpeg,image/png"
          onChange={(e) => onSelectFile(e.target.files?.[0])}
          disabled={disabled}
          className="hidden"
          data-testid="analyzer-file-input"
        />
        {file ? (
          <>
            <div className="text-emerald-300 text-sm font-medium">{file.name}</div>
            <div className="text-xs text-slate-500">
              {fileKind?.toUpperCase()} · {prettyMB(file.size)} MB · click to replace
            </div>
          </>
        ) : (
          <>
            <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0-12l-4 4m4-4l4 4"/>
            </svg>
            <div className="text-sm text-slate-300 font-medium">Drop a creative here or click to browse</div>
            <div className="text-xs text-slate-500">MP4 · MOV · JPG · PNG</div>
          </>
        )}
      </label>
      {fileError && (
        <p className="text-xs text-rose-300 mt-2" data-testid="analyzer-file-error">{fileError}</p>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Asset name field with per-segment green/red validators
// ----------------------------------------------------------------------------
/**
 * The asset name — PICKED, never typed.
 *
 * NAMING-SHARED-POOL, 2026-09-08. This was a free-text `<input>`, and
 * `analyzer.py`'s own docstring said so: *"CIPHER (operator types the validated
 * asset_name)"*. A typo silently attached an analysis to the wrong creative, or
 * to none. The Founder's ruling: *"this is a system-assigned value specifically
 * to remove human error — nobody should be able to override it by hand."*
 *
 * THE TRADEOFF, stated rather than hidden: an asset with no `creatives` or
 * `productions` row for its offer can no longer be analysed on the CIPHER path.
 * That is the lock working, and it is also a real loss. It surfaces as a
 * refusal a person can read instead of a typo nobody sees — but if it blocks
 * legitimate work, the NON_CIPHER path takes any asset and generates its own
 * name, and this list can be widened.
 *
 * The per-segment validators below are kept. They no longer guard against
 * typing, because typing is gone — they now confirm that what Cipher minted
 * actually parses, which is a different and still-useful check.
 */
const AssetNameField = ({ value, onChange, validation, disabled, offerId }) => {
  const [names, setNames] = React.useState(null);   // null = not loaded
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!offerId) { setNames(null); setError(null); return; }
    let alive = true;
    setNames(null);
    setError(null);
    axios
      .get(`${API}/analyzer/offers/${offerId}/asset-names`)
      .then((res) => { if (alive) setNames(res.data?.asset_names || []); })
      .catch((e) => {
        // Three states, not two: a failed read must not look like an offer
        // with no creatives.
        if (alive) setError(e?.response?.data?.detail || e?.message || "request failed");
      });
    return () => { alive = false; };
  }, [offerId]);

  return (
  <div className="space-y-3">
    {!offerId ? (
      <p className="text-xs text-slate-500" data-testid="analyzer-name-needs-offer">
        Choose an offer above first — the name is picked from what Cipher has
        generated for that offer.
      </p>
    ) : error ? (
      <p className="text-xs text-amber-400" data-testid="analyzer-name-load-failed">
        Could not read this offer's creative names: {error}. This is not the
        same as the offer having none — reload and try again.
      </p>
    ) : names === null ? (
      <p className="text-xs text-slate-500" data-testid="analyzer-name-loading">
        Reading this offer's creative names…
      </p>
    ) : names.length === 0 ? (
      <p className="text-xs text-amber-400" data-testid="analyzer-name-none">
        Cipher has not named any creative for this offer yet. Nothing can be
        analysed on the Cipher path until it has. If this is an external ad,
        switch to Non-Cipher above.
      </p>
    ) : (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid="analyzer-asset-name"
        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm font-mono tracking-wide text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
      >
        <option value="">Select the creative Cipher named…</option>
        {names.map((n) => (
          <option key={n.asset_name} value={n.asset_name}>{n.asset_name}</option>
        ))}
      </select>
    )}
    <div className="grid grid-cols-5 gap-1.5" data-testid="analyzer-segments">
      {validation.segments.map((s, i) => (
        <SegmentBadge key={i} seg={s} />
      ))}
    </div>
    {!validation.overall && (
      <ul className="text-xs text-slate-500 space-y-0.5 mt-2">
        {validation.segments
          .filter((s) => !s.ok && s.error && s.error !== "required")
          .map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-rose-400 mt-0.5">✕</span>
              <span><b className="text-slate-300">{s.label}:</b> {s.error}</span>
            </li>
          ))}
      </ul>
    )}
  </div>
  );
};

const SegmentBadge = ({ seg }) => {
  const isEmpty = !seg.raw;
  const cls = seg.ok
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
    : isEmpty
      ? "border-slate-800 bg-slate-950 text-slate-600"
      : "border-rose-500/40 bg-rose-500/10 text-rose-200";
  return (
    <div
      className={`border rounded-md px-2 py-1.5 text-[11px] flex flex-col gap-0.5 ${cls}`}
      data-testid={`analyzer-segment-${seg.label.toLowerCase().replace(/\s+/g, "-")}`}
      data-ok={seg.ok ? "true" : "false"}
    >
      <span className="uppercase text-[9px] tracking-wider opacity-80">{seg.label}</span>
      <span className="font-mono truncate">{seg.raw || "—"}</span>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Readiness checklist — tells the operator exactly why the button is disabled
// ----------------------------------------------------------------------------
const ReadinessChecklist = ({ file, fileError, validation, offerId, channel, objective }) => {
  const items = [
    { ok: !!file && !fileError, label: "Creative asset uploaded" },
    { ok: validation.overall, label: "Asset name passes naming convention" },
    { ok: !!offerId, label: "Offer association confirmed" },
    { ok: !!channel, label: "Traffic channel selected" },
    { ok: !!objective, label: "Campaign objective selected" },
  ];
  return (
    <div className="text-xs text-slate-500 space-y-1.5 mt-3" data-testid="analyzer-readiness">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={it.ok ? "text-emerald-400" : "text-slate-700"}>●</span>
          <span className={it.ok ? "text-slate-300 line-through opacity-60" : ""}>{it.label}</span>
        </div>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Results shell — four Section-6 sections with empty/queued/live states
// ----------------------------------------------------------------------------
const ResultsShell = ({ runState, fileKind, selectedOffer, channel, objective, assetName, prediction, runError }) => {
  if (runState === "idle") {
    return (
      <>
        <IntroPanel />
        <SectionCard
          title="Asset Dissection Summary"
          subtitle="Gemini multimodal dissection output"
        >
          <EmptyRows />
        </SectionCard>
        <SectionCard
          title="Persona Behavioral Traces"
          subtitle="3-4 synthetic personas simulated per analysis"
        >
          <PersonaSkeletonRow />
        </SectionCard>
        <SectionCard
          title="Pre-Launch Score"
          subtitle="Composite 0-100 score with Claude Haiku reasoning"
        >
          <ScoreSkeleton />
        </SectionCard>
        <SectionCard
          title="Decision Gate"
          subtitle="≥40 proceed · <40 requires override reasoning"
        >
          <DecisionGateSkeleton />
        </SectionCard>
      </>
    );
  }

  if (runState === "error") {
    return (
      <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5" data-testid="analyzer-error-banner">
        <h3 className="text-sm font-semibold text-rose-200">Analysis failed</h3>
        <p className="mt-2 text-xs text-rose-100/80 break-words">{runError || "Unknown error."}</p>
        <p className="mt-3 text-xs text-slate-400">Use Reset to start over.</p>
      </div>
    );
  }

  // QUEUED / RUNNING / COMPLETE — show live progress + populate as data arrives.
  const status = prediction?.status || (runState === "queued" ? "QUEUED" : "RUNNING");
  const dissection = prediction?.dissection;
  const traces = prediction?.persona_traces || [];
  const breakdown = prediction?.score_breakdown || [];
  const score = prediction?.composite_score;
  const tier = prediction?.score_tier;
  const reasoning = prediction?.score_reasoning;
  const mismatch = prediction?.message_offer_mismatch;
  const isComplete = runState === "complete";

  return (
    <>
      <RunStatusBanner
        status={status}
        assetName={prediction?.asset_name || assetName}
        fileKind={fileKind}
        selectedOffer={selectedOffer}
        channel={channel}
        objective={objective}
        isComplete={isComplete}
      />

      <SectionCard
        title="Asset Dissection Summary"
        subtitle="Gemini multimodal dissection output"
      >
        {dissection ? <DissectionView dissection={dissection} /> : <LoadingRows />}
      </SectionCard>

      <SectionCard
        title="Persona Behavioral Traces"
        subtitle="3-4 synthetic personas simulated per analysis"
        action={mismatch ? <Pill tone="amber">message-offer mismatch</Pill> : null}
      >
        {traces.length > 0 ? <PersonaTracesGrid traces={traces} /> : <PersonaSkeletonRow animated />}
      </SectionCard>

      <SectionCard
        title="Pre-Launch Score"
        subtitle="Composite 0-100 with objective-weighted attributes"
      >
        {typeof score === "number" ? (
          <ScoreView score={score} tier={tier} reasoning={reasoning} breakdown={breakdown} />
        ) : (
          <ScoreSkeleton animated />
        )}
      </SectionCard>

      <SectionCard
        title="Metadata & Download"
        subtitle="Embedded metadata removed, and the named file to upload to Meta"
      >
        <ScrubLayer prediction={prediction} />
      </SectionCard>

      {prediction?.source === "NON_CIPHER" && <NonCipherResults prediction={prediction} />}

      <SectionCard
        title="Decision Gate"
        subtitle={isComplete ? "Decide whether this asset proceeds to the pipeline" : "Awaiting score"}
      >
        {isComplete && typeof score === "number" ? (
          <DecisionGate
            predictionId={prediction?.prediction_id}
            score={score}
            tier={tier}
            decided={prediction?.decision === "PROCEEDED" || prediction?.decision === "OVERRIDDEN"}
            decision={prediction?.decision}
            driveWebLink={prediction?.drive_web_link}
            driveFolderUrl={prediction?.drive_folder_url}
            onDecided={(updated) => setPrediction(updated)}
          />
        ) : (
          <DecisionGateSkeleton animated />
        )}
      </SectionCard>
    </>
  );
};

// ============================================================================
// Metadata scrub + download (#200, #226)
//
// The rule this panel exists to honour: a failed strip must never look like a
// successful one. So the tone, the wording and whether the download button
// exists at all are ALL driven by `metadata_scrub.is_clean` — a single boolean
// the backend computes — and never by the absence of an error. There is no
// branch here that renders "clean" because nothing went wrong.
// ============================================================================
const SCRUB_COPY = {
  SCRUBBED: {
    tone: "green",
    headline: "Embedded metadata removed and verified gone.",
  },
  ALREADY_CLEAN: {
    tone: "green",
    headline: "This file carried no embedded metadata. Checked, not assumed.",
  },
  TOOL_UNAVAILABLE: {
    tone: "red",
    headline: "NOT SCRUBBED — no metadata tool on the server.",
  },
  VERIFY_FAILED: {
    tone: "red",
    headline: "NOT SCRUBBED — metadata survived the strip and the file was discarded.",
  },
  FAILED: {
    tone: "red",
    headline: "NOT SCRUBBED — the strip failed.",
  },
  UNSUPPORTED_KIND: {
    tone: "amber",
    headline: "NOT SCRUBBED — this file type has no scrubber.",
  },
  UNKNOWN: {
    tone: "amber",
    headline: "NOT SCRUBBED — no scrub was recorded for this upload.",
  },
};

const MetadataTable = ({ rows, emptyLabel }) => {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-slate-500 italic">{emptyLabel}</div>;
  }
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.key} className="flex items-start gap-2 text-xs">
          <span className="text-slate-500 w-40 shrink-0">{r.label}</span>
          <span className="font-mono text-slate-200 break-all">{r.value}</span>
        </div>
      ))}
    </div>
  );
};

// Exported as a named export purely so the panel can be tested on its own.
// The default export stays the whole analyzer, and both v1 `/analyzer` and the
// v2 Workstation surface still mount that unchanged and prop-free (PR #202).
export const ScrubLayer = ({ prediction }) => {
  const scrub = prediction?.metadata_scrub;
  const [downloadError, setDownloadError] = useState("");

  if (!scrub) {
    return (
      <div className="text-xs text-slate-500 italic" data-testid="scrub-pending">
        Awaiting upload.
      </div>
    );
  }

  const status = scrub.status || "UNKNOWN";
  const copy = SCRUB_COPY[status] || SCRUB_COPY.UNKNOWN;
  const clean = scrub.is_clean === true;
  const nameFinal =
    prediction?.source !== "NON_CIPHER" ||
    prediction?.asset_name_status === "FINALIZED";
  const canDownload = clean && nameFinal && Boolean(prediction?.prediction_id);

  const onDownload = async () => {
    setDownloadError("");
    try {
      const res = await axios.get(
        `${API}/analyzer/download/${prediction.prediction_id}`,
        { responseType: "blob" },
      );
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `${prediction?.asset_name || "creative"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // The backend refuses with a reason; show the reason, never a generic
      // "download failed" that hides why.
      const detail = e?.response?.data?.detail;
      setDownloadError(
        (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" && detail) ||
          "Download failed.",
      );
    }
  };

  return (
    <div className="space-y-4" data-testid="scrub-layer">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">
          Metadata scrub
        </span>
        {/* `Pill` does not forward arbitrary props, so the test hook goes on a
            wrapper rather than by changing a component ten other screens use. */}
        <span data-testid="scrub-status-pill">
          <Pill tone={copy.tone}>{status}</Pill>
        </span>
      </div>

      <p
        className={`text-xs ${clean ? "text-emerald-200" : "text-rose-200"}`}
        data-testid="scrub-headline"
      >
        {copy.headline}
      </p>
      {scrub.detail && (
        <p className="text-xs text-slate-400" data-testid="scrub-detail">
          {scrub.detail}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            Before
          </div>
          <MetadataTable
            rows={scrub.before_rows}
            emptyLabel="No embedded metadata found in the upload."
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            After
          </div>
          <MetadataTable
            rows={scrub.after_rows}
            emptyLabel={
              clean
                ? "Nothing — confirmed by re-reading the output file."
                : "Not checked: no clean file was produced."
            }
          />
        </div>
      </div>

      <div className="pt-2 border-t border-white/5">
        {canDownload ? (
          <button
            type="button"
            onClick={onDownload}
            className="btn-ghost btn-sm"
            data-testid="scrub-download"
          >
            Download “{prediction?.asset_name}” ↓
          </button>
        ) : (
          <p className="text-xs text-amber-200" data-testid="scrub-download-blocked">
            {!clean
              ? "No download: Cipher will not hand back a file it has not verified is clean."
              : "No download: the naming convention has not produced a final name for this ad yet."}
          </p>
        )}
        {downloadError && (
          <p className="text-xs text-rose-300 mt-2" data-testid="scrub-download-error">
            {downloadError}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// NON_CIPHER results — three clearly-separated layers:
//   Evidence (transcript/timestamps/on-screen/confidence)
//   Interpretation (Creative DNA, anatomy, strengths/weaknesses, adaptations)
//   Operations (final name, component status, Drive upload)
// ============================================================================
const confidenceTone = (v) =>
  typeof v !== "number" ? "slate" : v >= 0.75 ? "green" : v >= 0.5 ? "amber" : "red";

const ConfidencePill = ({ value, label }) => {
  if (typeof value !== "number") return null;
  return <Pill tone={confidenceTone(value)}>{label ? `${label} ` : ""}{Math.round(value * 100)}%</Pill>;
};

const Collapsible = ({ label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-800 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-300 hover:text-white"
        data-testid="nc-collapsible-toggle"
      >
        <span>{label}</span>
        <span className="text-slate-500">{open ? "▾ hide" : "▸ show"}</span>
      </button>
      {open && <div className="px-3 pb-3 pt-0">{children}</div>}
    </div>
  );
};

const KV = ({ label, value }) => {
  if (value === null || value === undefined || value === "" || value === "UNKNOWN") return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-500 shrink-0">{label}:</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
};

const ChipList = ({ label, items, tone = "slate" }) => {
  const clean = (items || []).filter((x) => x && x !== "UNKNOWN");
  if (clean.length === 0) return null;
  return (
    <div className="text-xs">
      <div className="text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {clean.map((x, i) => (
          <span key={i} className={`px-2 py-0.5 rounded border text-[11px] ${
            tone === "red" ? "bg-rose-500/10 text-rose-200 border-rose-500/25"
            : tone === "green" ? "bg-emerald-500/10 text-emerald-200 border-emerald-500/25"
            : "bg-slate-800/60 text-slate-300 border-slate-700"}`}>{x}</span>
        ))}
      </div>
    </div>
  );
};

const SegmentRow = ({ label, seg }) => {
  if (!seg) return null;
  const hasSpan = typeof seg.start === "number" && typeof seg.end === "number" && (seg.start || seg.end);
  return (
    <div className="border-l-2 border-slate-700 pl-3 py-1" data-testid={`nc-segment-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-300">{label}</span>
        {hasSpan && <span className="text-[10px] text-slate-500 font-mono">{seg.start}s–{seg.end}s</span>}
        <ConfidencePill value={seg.confidence} />
      </div>
      <div className="text-xs text-slate-200 mt-1">{seg.text || <span className="text-slate-500 italic">not detected</span>}</div>
    </div>
  );
};

const PendingReviewBanner = ({ prediction }) => {
  const rep = prediction?.creative_representation || {};
  const segs = rep.segments || {};
  const reasonMap = {
    evidence_unreliable: "The transcript evidence wasn't reliable enough to identify real components.",
    component_resolution_failed: "One or more components could not be resolved to a real component ID.",
    name_build_failed: "A canonical asset name could not be built from the resolved components.",
    exception: "An unexpected error occurred during component matching.",
  };
  const reason = reasonMap[prediction?.component_matching_failure_reason] || "Component matching could not complete.";

  // Which evidence was insufficient — concrete, not just a status label.
  const insufficiencies = [];
  if (typeof rep.transcript_quality_confidence === "number" && rep.transcript_quality_confidence < 0.55) {
    insufficiencies.push(`Transcript quality was low (${Math.round(rep.transcript_quality_confidence * 100)}%, need ≥55%).`);
  }
  ["hook", "cta"].forEach((k) => {
    const s = segs[k] || {};
    if (!s.text) insufficiencies.push(`No ${k.toUpperCase()} could be segmented from the creative.`);
    else if (typeof s.confidence === "number" && s.confidence < 0.5)
      insufficiencies.push(`${k.toUpperCase()} segmentation confidence was low (${Math.round(s.confidence * 100)}%, need ≥50%).`);
  });
  (rep.warnings || []).forEach((w) => { if (w && !w.startsWith("low_reliability")) insufficiencies.push(w); });

  return (
    <div className="rounded-xl border-2 border-amber-500/60 bg-amber-500/10 p-5" data-testid="nc-pending-review-banner">
      <div className="flex items-center gap-2">
        <span className="text-lg">⚠️</span>
        <h3 className="text-sm font-bold text-amber-200 uppercase tracking-wide">Asset name pending review — components not classified</h3>
      </div>
      <p className="mt-2 text-xs text-amber-100/90">{reason} Cipher did <b>not</b> fabricate component IDs; a temporary internal filename is being used for storage until this is reviewed.</p>
      {insufficiencies.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] font-semibold text-amber-200/80 uppercase">Insufficient evidence</div>
          <ul className="mt-1 list-disc list-inside text-xs text-amber-100/90 space-y-0.5">
            {insufficiencies.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-3 text-[11px] text-amber-200/70 font-mono">temp name: {prediction?.asset_name}</div>
    </div>
  );
};

const EvidenceLayer = ({ representation }) => {
  if (!representation) {
    return <div className="text-xs text-slate-500 italic" data-testid="nc-evidence-empty">Transcription evidence not available for this asset.</div>;
  }
  const segs = representation.segments || {};
  const onScreen = (representation.on_screen_text || []).filter(Boolean);
  const transcript = representation.full_transcript || "";
  const tst = representation.timestamped_transcript || [];
  return (
    <div className="space-y-3" data-testid="nc-evidence-layer">
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone="slate">{(representation.representation_kind || "").toUpperCase()}</Pill>
        {representation.transcript_quality_confidence !== null &&
          <ConfidencePill value={representation.transcript_quality_confidence} label="transcript" />}
        <Pill tone={representation.reliable ? "green" : "amber"}>{representation.reliable ? "reliable" : "unreliable"}</Pill>
      </div>

      {/* Hook / Body / CTA visible upfront — the transcript itself is collapsed. */}
      <div className="space-y-1.5">
        <SegmentRow label="Hook" seg={segs.hook} />
        <SegmentRow label="Body" seg={segs.body} />
        <SegmentRow label="CTA" seg={segs.cta} />
      </div>

      {onScreen.length > 0 && <ChipList label="On-screen text" items={onScreen} />}

      {transcript && (
        <Collapsible label={`Full spoken transcript (${transcript.length} chars)`}>
          <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{transcript}</p>
        </Collapsible>
      )}
      {tst.length > 0 && (
        <Collapsible label={`Timestamped transcript (${tst.length} segments)`}>
          <div className="space-y-1">
            {tst.map((s, i) => (
              <div key={i} className="text-xs text-slate-300 flex gap-2">
                <span className="text-slate-500 font-mono shrink-0">{s.start}s–{s.end}s</span>
                <span>{s.text}</span>
              </div>
            ))}
          </div>
        </Collapsible>
      )}
    </div>
  );
};

const InterpretationLayer = ({ intelligence, dna, validationStatus }) => {
  const intel = intelligence || {};
  const failed = validationStatus === "EXTRACTION_FAILED";
  return (
    <div className="space-y-4" data-testid="nc-interpretation-layer">
      {failed && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
          Intelligence extraction was low-quality and is excluded from the Knowledge Library (not retrievable).
        </div>
      )}

      {/* Creative DNA */}
      {dna && (
        <div className="rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-3" data-testid="nc-creative-dna">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-300">Creative DNA</span>
            {dna.dna_id && <span className="font-mono text-xs text-indigo-200">{dna.dna_id}</span>}
            {dna.match_type && <Pill tone={dna.match_type === "NOVEL" ? "green" : dna.match_type === "EXACT" ? "amber" : "indigo"}>{dna.match_type}</Pill>}
          </div>
          <div className="mt-2 space-y-0.5">
            {dna.parent_dna_id && <KV label="Variation of" value={dna.parent_dna_id} />}
            {typeof dna.observation_count === "number" && <KV label="Times observed" value={dna.observation_count} />}
            {typeof dna.closest_overlap === "number" && <KV label="Closest overlap" value={`${Math.round(dna.closest_overlap * 100)}%`} />}
          </div>
        </div>
      )}

      {/* Creative anatomy */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4">
        <KV label="Primary audience" value={intel.primary_audience} />
        <KV label="Awareness level" value={intel.awareness_level} />
        <KV label="Core problem" value={intel.primary_problem} />
        <KV label="Emotional driver" value={intel.emotional_driver} />
        <KV label="Core promise" value={intel.core_promise} />
        <KV label="Mechanism" value={intel.mechanism} />
        <KV label="Creative angle" value={intel.creative_angle} />
        <KV label="Hook structure" value={intel.hook_structure} />
        <KV label="Visual format" value={intel.visual_format} />
        <KV label="Messenger" value={intel.creator_messenger_type} />
        <KV label="Offer–message alignment" value={intel.offer_message_alignment} />
        <KV label="Proof mechanism" value={intel.proof_mechanism} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ChipList label="Strengths" items={intel.strengths} tone="green" />
        <ChipList label="Weaknesses" items={intel.weaknesses} tone="red" />
      </div>

      <div className="space-y-1">
        <KV label="Why it works" value={intel.why_working} />
        <KV label="Why it may fail" value={intel.why_failing} />
      </div>

      <Collapsible label="What Cipher should learn (adaptations, hypotheses, reuse)">
        <div className="space-y-3">
          <ChipList label="Recommended Cipher adaptations" items={intel.recommended_cipher_adaptations} />
          <ChipList label="Reusable elements" items={intel.reusable_elements} tone="green" />
          <ChipList label="Offer-specific (don't generalize)" items={intel.offer_specific_elements} />
          <ChipList label="Do NOT copy" items={intel.elements_to_avoid_copying} tone="red" />
          <ChipList label="Testable hypotheses" items={intel.testable_hypotheses} />
          <ChipList label="Compliance risks" items={intel.compliance_risks} tone="red" />
        </div>
      </Collapsible>
    </div>
  );
};

const componentRows = (assoc) => {
  if (!assoc) return [];
  return [["Hook", assoc.hook], ["Body", assoc.body], ["CTA", assoc.cta]]
    .filter(([, v]) => v)
    .map(([label, v]) => ({ label, ...v }));
};

const OperationsLayer = ({ prediction }) => {
  const nameStatus = prediction?.asset_name_status;
  const matchStatus = prediction?.component_matching_status;
  const rows = componentRows(prediction?.component_associations);
  const driveStatus = prediction?.drive_upload_status;
  const driveTone = driveStatus === "UPLOADED" || driveStatus === "SKIPPED_EXISTS" ? "green"
    : driveStatus === "FAILED" ? "red" : "amber";
  return (
    <div className="space-y-4" data-testid="nc-operations-layer">
      {/* Final name */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Asset name</span>
          {nameStatus && <Pill tone={nameStatus === "FINALIZED" ? "green" : "amber"}>{nameStatus}</Pill>}
        </div>
        <div className="font-mono text-xs text-slate-100 break-all">{prediction?.asset_name}</div>
      </div>

      {/* Component matching */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Component matching</span>
          {matchStatus && <Pill tone={matchStatus === "COMPLETE" ? "green" : "red"}>{matchStatus}</Pill>}
        </div>
        {rows.length > 0 ? (
          <div className="space-y-1">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center gap-2 text-xs" data-testid={`nc-comp-${r.label.toLowerCase()}`}>
                <span className="text-slate-500 w-10">{r.label}</span>
                <span className="font-mono text-slate-200">{r.element_number || "—"}</span>
                {r.action && <Pill tone={r.action === "REUSED" ? "indigo" : r.action === "CREATED" ? "green" : "slate"}>{r.action}</Pill>}
                <ConfidencePill value={r.confidence} label="conf" />
                {r.source_evidence?.start !== undefined && r.source_evidence?.start !== null && (
                  <span className="text-[10px] text-slate-500 font-mono">{r.source_evidence.start}s–{r.source_evidence.end}s</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">No components were classified.</div>
        )}
      </div>

      {/* Drive */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Google Drive</span>
          {driveStatus ? <Pill tone={driveTone}>{driveStatus}</Pill> : <Pill tone="slate">pending</Pill>}
        </div>
        {prediction?.drive_web_url && (
          <a href={prediction.drive_web_url} target="_blank" rel="noreferrer"
             className="text-xs text-indigo-300 hover:text-indigo-200 underline break-all" data-testid="nc-drive-link">
            Open in Drive ↗
          </a>
        )}
        {prediction?.drive_upload_partial_success && (
          <p className="text-xs text-amber-200 mt-1">
            Upload did not complete{prediction?.drive_upload_retryable ? " (retryable)" : ""}.
            {prediction?.drive_upload_error ? ` ${prediction.drive_upload_error}` : ""} The analysis result is unaffected.
          </p>
        )}
        {prediction?.drive_uploaded_at && (
          <div className="text-[10px] text-slate-500 mt-1">uploaded {String(prediction.drive_uploaded_at).replace("T", " ").slice(0, 19)}</div>
        )}
      </div>
    </div>
  );
};

const NonCipherResults = ({ prediction }) => {
  const pending = prediction?.asset_name_status === "PENDING_REVIEW";
  return (
    <div className="space-y-6" data-testid="analyzer-noncipher-results">
      {pending && <PendingReviewBanner prediction={prediction} />}

      <SectionCard title="External Creative — Evidence" subtitle="Transcript, segmentation & on-screen text (source of truth)">
        <EvidenceLayer representation={prediction?.creative_representation} />
      </SectionCard>

      <SectionCard title="External Creative — Interpretation" subtitle="Creative DNA, anatomy & what Cipher should learn (observational)">
        <InterpretationLayer
          intelligence={prediction?.noncipher_intelligence}
          dna={prediction?.creative_dna}
          validationStatus={prediction?.intelligence_validation_status}
        />
      </SectionCard>

      <SectionCard title="External Creative — Operations" subtitle="Naming, component classification & Drive storage">
        <OperationsLayer prediction={prediction} />
      </SectionCard>
    </div>
  );
};

const RunStatusBanner = ({ status, assetName, fileKind, selectedOffer, channel, objective, isComplete }) => {
  const tone = isComplete ? "emerald" : "indigo";
  return (
    <div className={`rounded-xl p-5 border ${
      isComplete
        ? "bg-emerald-500/10 border-emerald-500/30"
        : "bg-indigo-500/10 border-indigo-500/30"
    }`}>
      <div className="flex items-center gap-3">
        <span className={`inline-block w-2 h-2 rounded-full ${isComplete ? "bg-emerald-400" : "bg-indigo-400 animate-pulse"}`} />
        <h3 className={`text-sm font-semibold ${tone === "emerald" ? "text-emerald-200" : "text-indigo-200"}`}
            data-testid="analyzer-run-status-banner">
          {isComplete ? "Analysis complete" : `Analysis in progress — ${status}`}
        </h3>
      </div>
      <div className="mt-3 text-xs text-slate-400 grid grid-cols-2 gap-2">
        <div><b className="text-slate-300">Asset:</b> <span className="font-mono">{assetName}</span></div>
        <div><b className="text-slate-300">Type:</b> {fileKind?.toUpperCase() || "—"}</div>
        <div><b className="text-slate-300">Offer:</b> {selectedOffer?.offer_code || "—"}</div>
        <div><b className="text-slate-300">Channel / Objective:</b> {channel} / {objective}</div>
      </div>
    </div>
  );
};

const DissectionView = ({ dissection }) => {
  // Render a flexible dictionary view — image and video share most fields.
  const entries = Object.entries(dissection).filter(([k]) => !k.startsWith("_") && k !== "vertical_attribute_scores");
  return (
    <div className="space-y-3" data-testid="analyzer-dissection-view">
      {entries.map(([k, v]) => (
        <div key={k} className="border-l-2 border-slate-700 pl-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {k.replace(/_/g, " ")}
          </div>
          {typeof v === "string" ? (
            <div className="text-xs text-slate-200 mt-1">{v}</div>
          ) : v && typeof v === "object" ? (
            <div className="text-xs text-slate-300 mt-1 space-y-0.5">
              {Object.entries(v).map(([sk, sv]) => (
                <div key={sk}>
                  <span className="text-slate-500">{sk.replace(/_/g, " ")}:</span>{" "}
                  <span>{typeof sv === "string" ? sv : JSON.stringify(sv)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-300 mt-1">{String(v)}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const PersonaTracesGrid = ({ traces }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="analyzer-persona-traces">
    {traces.map((t) => (
      <PersonaCard key={t.persona_id} trace={t} />
    ))}
  </div>
);

const PersonaCard = ({ trace }) => {
  const isSecondary = trace.role === "secondary";
  const intentTone = trace.click_intent === "HIGH" ? "green" : trace.click_intent === "MEDIUM" ? "amber" : "red";
  const convTone = trace.conversion_alignment === "HIGH" ? "green" : trace.conversion_alignment === "MEDIUM" ? "amber" : "red";
  return (
    <div className={`rounded-lg p-3 bg-slate-950/60 border ${
      isSecondary ? "border-amber-500/40" : "border-slate-800"
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white truncate">{trace.persona_name}</div>
          <div className="text-[11px] text-slate-500">{trace.niche} · {trace.role}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase text-slate-500">Thumb stop</div>
          <div className={`text-xs font-semibold ${trace.thumb_stop === "YES" ? "text-emerald-300" : "text-rose-300"}`}>
            {trace.thumb_stop} ({trace.thumb_stop_confidence_pct}%)
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Pill tone={intentTone}>click {trace.click_intent}</Pill>
        <Pill tone={convTone}>conv {trace.conversion_alignment}</Pill>
        {trace.retention && <Pill tone="slate">retention {trace.retention}</Pill>}
      </div>
      <p className="mt-2 text-[11px] text-slate-300 leading-relaxed">{trace.behavioral_note}</p>
    </div>
  );
};

const ScoreView = ({ score, tier, reasoning, breakdown }) => {
  const tone = tier === "GREEN" ? "emerald" : tier === "AMBER" ? "amber" : "rose";
  const ring = {
    emerald: "from-emerald-500/30 to-emerald-500/0 ring-emerald-500/40 text-emerald-300",
    amber: "from-amber-500/30 to-amber-500/0 ring-amber-500/40 text-amber-300",
    rose: "from-rose-500/30 to-rose-500/0 ring-rose-500/40 text-rose-300",
  }[tone];
  return (
    <div data-testid="analyzer-score-view">
      <div className="flex items-center gap-4">
        <div className={`flex items-center justify-center w-24 h-24 rounded-full ring-2 bg-gradient-to-b ${ring}`}>
          <div className="text-3xl font-bold">{score}</div>
        </div>
        <div className="space-y-1">
          <Pill tone={tone === "emerald" ? "green" : tone === "amber" ? "amber" : "red"}>
            {tier}
          </Pill>
          <div className="text-[11px] text-slate-500">composite / 100</div>
        </div>
      </div>
      {reasoning && (
        <p className="mt-3 text-sm text-slate-200 leading-relaxed" data-testid="analyzer-score-reasoning">
          {reasoning}
        </p>
      )}
      {Array.isArray(breakdown) && breakdown.length > 0 && (
        <div className="mt-4 space-y-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">attribute breakdown</div>
          {breakdown.map((b) => (
            <div key={b.key} className="flex items-center justify-between text-xs gap-3">
              <div className="text-slate-300 truncate">{b.display_name}</div>
              <div className="text-slate-500 font-mono shrink-0">
                {b.raw_score_0_100} × {b.normalized_weight.toFixed(3)} = {b.weighted_contribution.toFixed(1)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DecisionGate = ({ predictionId, score, tier, decided, decision, driveWebLink, driveFolderUrl, onDecided }) => {
  const [reasoning, setReasoning] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const proceedAllowed = score >= 40;
  const charCount = reasoning.trim().length;
  const overrideReady = charCount >= 50;
  const buttonEnabled = !decided && (proceedAllowed || overrideReady);

  const handleClick = async () => {
    if (!buttonEnabled || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = proceedAllowed
        ? { decision: "PROCEED" }
        : { decision: "OVERRIDE", override_reasoning: reasoning.trim() };
      const res = await axios.post(`${API}/analyzer/decide/${predictionId}`, body);
      onDecided?.(res.data);
    } catch (err) {
      console.error("analyzer.decide failed:", err);
      setError(err?.response?.data?.detail || err?.message || "Decision failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (decided) {
    const isOverride = decision === "OVERRIDDEN";
    return (
      <div className="space-y-3" data-testid="analyzer-decision-complete">
        <div className={`rounded-lg p-3 border ${
          isOverride
            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
        }`}>
          <div className="text-sm font-semibold">
            {isOverride ? "Manual override executed" : "Proceeded to pipeline"}
          </div>
          <div className="text-xs mt-1 text-slate-300">
            Asset moved to the offer/channel /VE Drive folder. Reconciliation in 14 days.
          </div>
        </div>
        {(driveWebLink || driveFolderUrl) && (
          <a
            href={driveWebLink || driveFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="analyzer-drive-folder-link"
            className="block text-center w-full px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/40 text-indigo-100 transition-colors"
          >
            Open in Google Drive →
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="analyzer-decision-gate">
      {!proceedAllowed && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-100" data-testid="analyzer-below-threshold-banner">
          This asset scored below the confidence threshold (Score: <b>{score}/100</b>). Provide your reasoning to proceed.
        </div>
      )}
      {!proceedAllowed && (
        <div>
          <label className="text-xs font-semibold text-slate-300 uppercase tracking-wide" htmlFor="override-reasoning">
            Why do you believe this creative will perform?
          </label>
          <textarea
            id="override-reasoning"
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            rows={4}
            data-testid="analyzer-override-textarea"
            className="mt-1 w-full bg-slate-950/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60"
            placeholder="The opening establishes credibility through the licensed-agent identifier in the first 2 seconds, which the personas missed because the audio cue was subtle…"
          />
          <div className="mt-1 flex items-center justify-between">
            <div
              data-testid="analyzer-override-charcount"
              className={`text-xs font-mono ${overrideReady ? "text-emerald-300" : "text-slate-500"}`}
            >
              {charCount} / 50 minimum
            </div>
            {overrideReady && (
              <Pill tone="green">Override threshold met</Pill>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-100" data-testid="analyzer-decision-error">
          {error}
        </div>
      )}

      <button
        type="button"
        disabled={!buttonEnabled || submitting}
        onClick={handleClick}
        data-testid="analyzer-proceed-pipeline-btn"
        className={`w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
          !buttonEnabled || submitting
            ? "bg-slate-800 text-slate-500 cursor-not-allowed"
            : proceedAllowed
            ? "bg-emerald-500/30 hover:bg-emerald-500/40 border border-emerald-500/50 text-emerald-100"
            : "bg-amber-500/30 hover:bg-amber-500/40 border border-amber-500/50 text-amber-100"
        }`}
      >
        {submitting ? "Submitting…" : "Proceed to Pipeline"}
      </button>
    </div>
  );
};

const IntroPanel = () => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
    <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">
      How this works
    </h3>
    <ol className="space-y-2 text-xs text-slate-400 list-decimal list-inside">
      <li>Gemini 2.5 performs structured multimodal dissection of the asset.</li>
      <li>Three to four synthetic buyer personas simulate engagement against the dissection.</li>
      <li>Objective-weighted composite score (0–100) is generated with Claude Haiku reasoning.</li>
      <li>Decision gate: ≥40 proceeds, &lt;40 requires a documented override.</li>
      <li>14 days post-launch the prediction is reconciled against live CTR/CVR data — personas
          self-calibrate over time.</li>
    </ol>
  </div>
);

const EmptyRows = () => (
  <div className="space-y-2">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="h-3 rounded bg-slate-800/50" style={{ width: `${80 - i * 8}%` }} />
    ))}
    <p className="text-xs text-slate-600 mt-3">Awaiting analysis.</p>
  </div>
);

const LoadingRows = () => (
  <div className="space-y-2 animate-pulse">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="h-3 rounded bg-slate-800" style={{ width: `${80 - i * 8}%` }} />
    ))}
  </div>
);

const PersonaSkeletonRow = ({ animated = false }) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${animated ? "animate-pulse" : ""}`}>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="bg-slate-950 border border-slate-800 rounded-lg p-4">
        <div className="h-3 w-24 rounded bg-slate-800 mb-2" />
        <div className="h-2 w-full rounded bg-slate-800/70 mb-1" />
        <div className="h-2 w-3/4 rounded bg-slate-800/70 mb-3" />
        <div className="flex gap-2">
          <div className="h-5 w-12 rounded-full bg-slate-800" />
          <div className="h-5 w-16 rounded-full bg-slate-800" />
        </div>
      </div>
    ))}
  </div>
);

const ScoreSkeleton = ({ animated = false }) => (
  <div className={`flex items-center gap-6 ${animated ? "animate-pulse" : ""}`}>
    <div className="w-24 h-24 rounded-full bg-slate-800/70 flex items-center justify-center text-slate-600 text-2xl font-bold">
      —
    </div>
    <div className="flex-1 space-y-2">
      <div className="h-3 w-full rounded bg-slate-800/70" />
      <div className="h-3 w-5/6 rounded bg-slate-800/70" />
      <div className="h-3 w-2/3 rounded bg-slate-800/70" />
    </div>
  </div>
);

const DecisionGateSkeleton = ({ animated = false }) => (
  <div className={`flex items-center gap-3 ${animated ? "animate-pulse" : ""}`}>
    <div className="h-9 flex-1 rounded-lg bg-slate-800/70" />
    <div className="h-9 w-28 rounded-lg bg-slate-800/70" />
  </div>
);
